/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import {
  CellType,
  ComponentType,
  IMediaCategoryListModel,
  IMediaCategoryModel,
  IMediaListModel,
  Orientation,
} from "@nf/common";
import { isServerSideRender } from "@nf/helpers";
import dynamic from "next/dynamic";
import { useMemo } from "react";
import InfiniteScroll from "react-infinite-scroll-component";

import { GridComponent } from "components/Configuration";
import DotsLoader from "components/DotsLoader";
import CategoryDropdown from "components/Dropdown/CategoryDropdown";
import SortingDropdown from "components/Dropdown/SortingDropdown";
import MediaArticleField from "components/Media/MediaArticleField";
import GenreSEO from "components/SEO/GenreSEO";
import {
  useBrandingChangeEvent,
  useGenreRouter,
  useGetSortedList,
  useInfiniteScrollData,
  useUpdateMediaListStore,
} from "hooks";

const ActionsHandler = dynamic(() => import("components/ActionsHandler"), {
  ssr: false,
});

import styles from "./GenresScreen.module.scss";

export interface GenresScreenProps {
  categories: IMediaCategoryListModel;
  category: IMediaCategoryModel;
  mediaList: IMediaListModel;
}

export const GenresScreen = ({
  categories,
  category,
  mediaList: mediaListFromProps,
}: GenresScreenProps) => {
  const { mediaListId, params } = useGenreRouter();

  useUpdateMediaListStore(mediaListId, mediaListFromProps);

  const queryParams = {
    ...params,
    category: mediaListId,
  };

  const { mediaList, getList, getMore } = useInfiniteScrollData(
    queryParams,
    mediaListId
  );

  useGetSortedList(getList);
  useBrandingChangeEvent(mediaListId);

  const mediaListData = isServerSideRender ? mediaListFromProps : mediaList;

  const hasMore = useMemo(() => {
    return (
      !mediaListData.IsLoading &&
      !!mediaListData.Entities &&
      mediaListData?.Entities.length < mediaListData.TotalCount
    );
  }, [mediaListData]);

  return (
    <>
      <GenreSEO
        title={category.CategoryName}
        description={category.CategoryDescription}
      />

      <div className={styles.container}>
        <div className={styles.filters}>
          <div>
            <CategoryDropdown
              categories={categories}
              activeCategory={category}
            />
          </div>
          <div>
            <SortingDropdown />
          </div>
        </div>

        <InfiniteScroll
          className={styles.grid}
          dataLength={mediaListData?.Entities?.length || 0}
          next={getMore}
          hasMore={hasMore}
          scrollThreshold={0.6}
          loader={
            <div className={styles.loader}>
              <DotsLoader width={75} />
            </div>
          }
        >
          {!isServerSideRender && (
            <ActionsHandler centeredError error={mediaListData.Error} />
          )}

          <GridComponent
            component={{
              ComponentTypeCode: ComponentType.List,
              CellType: CellType.Frame,
              Orientation: Orientation.Grid,
              MediaList: mediaListData?.Entities,
            }}
          />
        </InfiniteScroll>

        <MediaArticleField article={category.Article} />
      </div>
    </>
  );
};

export default GenresScreen;
